/**
 * RPE restore (2026-06-09) — `<RpePrompt>`.
 *
 * The React successor to vanilla's workout-modal RPE step
 * (`plan-actions.js`): after the athlete marks a session done, this
 * compact overlay asks "How hard did it feel?" with the 1-10 chip ramp.
 * Copy + the green→yellow→orange→red banding are ported verbatim from
 * vanilla; the surface is NOT a blocking modal — completion has already
 * committed by the time this renders, and every dismiss path (Skip,
 * backdrop, Esc) simply closes it.
 *
 * Tier behavior (BellTrigger precedent): bottom sheet on touch
 * (<1024px), compact centered card on pointer — one DOM, CSS switches.
 *
 * Guardrails (design-pass-guardrails.md): chips ≥44px, role="dialog" +
 * aria-label, Esc dismiss, numbers always visible (color is never the
 * only indicator), spring-in respects prefers-reduced-motion.
 *
 * B335 — the scale words are anchored to the pills they name. The ten chips are
 * a 5 by 2 grid, and a separate "Very easy / Moderate / Max" row under it put
 * "Very easy" under 6 and "Max" under 10. Each anchor now lives in the cell of
 * its own chip (1, 5 and 10), on the outer edge of the grid so it never sits
 * between two rows. The amber and orange chips carry dark text (white on amber
 * was 2.57:1), and the words are read to assistive tech as the chip's
 * description, so meaning is not carried by colour alone.
 *
 * 2.0 effort sheet (W1-TODAY-EFFORT, frames 1h and 1i): an OPT-IN `variant="v2"`.
 * Absent, nothing below changes: the legacy sheet renders and its tests stay
 * green unmodified. Present, the same hooks, the same commit contract and the
 * same two modes (capture and low-friction) drive a different surface: a title
 * and a sub-line built from the session, ten neutral pills each carrying a short
 * band bar, the words "Very easy" over 1, "Moderate" over 5, "Hard" under 7 and
 * "Max" under 10, and, only when the distance question exists, a numeric field
 * with the athlete's unit as plain text and a "Planned" line under it. There is
 * no unit menu. The words' case rides `--label-case` (tokens.css, switched by
 * html[data-type-scale="ax"]); nothing here uppercases in JS.
 */

import { useEffect, useId, useState } from 'react';
import styles from './RpePrompt.module.css';

/**
 * AD-2.1 PR1 — optional actual-distance capture config. When present (the
 * `actual_distance_capture_v1` server flag is on AND the day is a
 * distance-running session), the prompt renders a "What did you actually run?"
 * field below the RPE chips, prefilled (but UNCONFIRMED) with the planned
 * distance.
 *
 * F-110(a) — in this CAPTURE mode a chip tap only SELECTS (visual state); a
 * single explicit Save commits BOTH the picked RPE and the typed distance
 * atomically, then the sheet closes. (The founder's loss case: tapping a chip
 * used to commit RPE + close, silently discarding a typed distance.)
 */
export interface RpeDistanceCapture {
  /** Planned distance to prefill (null → empty input). */
  plannedValue: number | null;
  /** Planned/display unit. */
  unit: 'mi' | 'km';
}

/** F-110(a) — the atomic capture payload the single Save commits. */
export interface RpeCaptureResult {
  /** Selected 1-10 rating, or null when none was picked. */
  rpe: number | null;
  /** Confirmed actual distance, or null when none typed / invalid. */
  distance: { value: number; unit: 'mi' | 'km' } | null;
}

/**
 * 2.0 effort sheet: the planned distance behind the "Planned 7 km" line under the
 * distance field. Value and unit, in the athlete's unit (the Settings unit).
 */
export interface RpePlannedDistance {
  value: number;
  unit: 'mi' | 'km';
}

export interface RpePromptProps {
  /**
   * 2.0 effort sheet (frames 1h, 1i). Absent: the legacy sheet, unchanged.
   * `'v2'`: the 2.0 sheet. Mounted by W1-TODAY-WIRE under `today_2_0_v1`.
   */
  variant?: 'v2';
  /**
   * v2 only. The sub-line under the question, session and planned distance in
   * one string (for example "Easy run · 7 km"). Falls back to `workoutLabel`;
   * with neither there is no sub-line.
   */
  sessionLabel?: string | null;
  /**
   * v2 only. The planned distance for the "Planned 7 km" line under the distance
   * field. Absent, null or not above zero: the line is not rendered, because
   * "Planned" never appears without a value. Used only where the distance
   * question exists (`distanceCapture` present).
   */
  plannedDistance?: RpePlannedDistance | null;
  /** Workout label shown under the question (e.g. "Easy Run"). */
  workoutLabel?: string | null;
  /**
   * F-110(a) — commit the capture in ONE write. Two modes:
   *  - CAPTURE (distanceCapture present): chip tap only selects; the explicit
   *    Save fires this once with {rpe, distance} and the parent closes.
   *  - LOW-FRICTION (no distanceCapture): a chip tap fires this immediately with
   *    {rpe, distance:null} and the parent closes (unchanged single-tap path).
   */
  onCommit: (result: RpeCaptureResult) => void;
  /** Called on Skip / backdrop / Esc. Nothing is saved. */
  onDismiss: () => void;
  /** AD-2.1 — optional actual-distance capture (absent → no distance UI). */
  distanceCapture?: RpeDistanceCapture | null;
  /**
   * F-110(d) — prefill a previously-logged RPE when the sheet is REOPENED to
   * correct an existing rating (founders log corrections). Absent/null → the
   * post-completion first-capture case (nothing pre-selected).
   */
  initialRpe?: number | null;
}

/** Vanilla `rpeColorClass` banding: 1-3 easy · 4-6 moderate · 7-8 hard · 9-10 max. */
export function rpeBand(value: number): 'easy' | 'moderate' | 'hard' | 'max' {
  if (value <= 3) return 'easy';
  if (value <= 6) return 'moderate';
  if (value <= 8) return 'hard';
  return 'max';
}

const RPE_VALUES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

/**
 * B335 — the scale words and the chip each one belongs to. `above` sits over the
 * first row (chips 1 and 5), `below` under the second (chip 10): the outer edges
 * of the grid, so a word is never read as the heading of the row beneath it.
 */
const RPE_ANCHORS: Partial<
  Record<(typeof RPE_VALUES)[number], { text: string; place: 'above' | 'below'; align: 'start' | 'end' }>
> = {
  1: { text: 'Very easy', place: 'above', align: 'start' },
  5: { text: 'Moderate', place: 'above', align: 'end' },
  10: { text: 'Max', place: 'below', align: 'end' },
};

/**
 * 2.0 (Appendix B rows 77 to 80): the four scale words, sentence case in the DOM
 * (the case on screen is CSS, through `--label-case`). "Very easy" and "Moderate"
 * sit in a row above the grid, over pills 1 and 5; "Hard" and "Max" sit in a row
 * under it, under pills 7 and 10. A word is never between two rows of pills.
 */
const RPE_V2_ANCHORS_ABOVE = [
  { value: 1, text: 'Very easy' },
  { value: 5, text: 'Moderate' },
] as const;
const RPE_V2_ANCHORS_BELOW = [
  { value: 7, text: 'Hard' },
  { value: 10, text: 'Max' },
] as const;
const RPE_V2_ANCHOR_VALUES: ReadonlySet<number> = new Set(
  [...RPE_V2_ANCHORS_ABOVE, ...RPE_V2_ANCHORS_BELOW].map((a) => a.value),
);

/** 2.0: a planned distance as it reads in "Planned 7 km" (one decimal at most). */
function formatPlanned(value: number): string {
  return String(Number(value.toFixed(1)));
}

export function RpePrompt({
  variant,
  sessionLabel,
  plannedDistance,
  workoutLabel,
  onCommit,
  onDismiss,
  distanceCapture,
  initialRpe = null,
}: RpePromptProps) {
  // CAPTURE mode = an actual-distance field is in play; a chip only SELECTS and
  // the explicit Save commits both. LOW-FRICTION mode (no distance) keeps the
  // single-tap chip-commit.
  const captureMode = !!distanceCapture;
  const anchorIdBase = useId();
  // Selected chip. F-110(d): seeded with any existing RPE when reopened to edit.
  const [picked, setPicked] = useState<number | null>(initialRpe);
  // AD-2.1 — actual-distance field. Prefilled but UNCONFIRMED: it only persists
  // on the explicit Save, never on chip-pick or dismiss.
  const [distText, setDistText] = useState<string>(
    distanceCapture && distanceCapture.plannedValue != null
      ? String(distanceCapture.plannedValue)
      : '',
  );
  const [distUnit, setDistUnit] = useState<'mi' | 'km'>(distanceCapture?.unit ?? 'mi');
  // Guards a double-commit (one Save / one low-friction chip tap).
  const [committed, setCommitted] = useState<boolean>(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onDismiss();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  const distValue = Number(distText);
  const distValid = distText.trim() !== '' && Number.isFinite(distValue) && distValue >= 0;
  const distancePayload = distValid ? { value: distValue, unit: distUnit } : null;

  const handlePick = (value: number): void => {
    if (committed) return;
    if (captureMode) {
      // F-110(a) — SELECT only. No commit, no close; Save owns the commit.
      // Re-tappable (correct a mis-pick before Save).
      setPicked(value);
      return;
    }
    // LOW-FRICTION — single chip tap commits + closes (unchanged).
    setPicked(value);
    setCommitted(true);
    onCommit({ rpe: value, distance: null });
  };

  // Save enabled once there is anything to write — a picked RPE OR a valid
  // distance. F-110(a): chip + Save → RPE persists even with no distance typed.
  const canSave = picked !== null || distancePayload !== null;
  const handleSave = (): void => {
    if (committed || !canSave) return;
    setCommitted(true);
    onCommit({ rpe: picked, distance: distancePayload });
  };

  if (variant === 'v2') {
    const sub = sessionLabel || workoutLabel || null;
    const planned =
      distanceCapture && plannedDistance && Number.isFinite(plannedDistance.value) && plannedDistance.value > 0
        ? `Planned ${formatPlanned(plannedDistance.value)} ${plannedDistance.unit}`
        : null;
    const anchorId = (value: number): string => `${anchorIdBase}-anchor-${value}`;
    const plannedId = `${anchorIdBase}-planned`;
    const unitId = `${anchorIdBase}-unit`;
    const describedBy = distanceCapture ? [unitId, planned ? plannedId : null].filter(Boolean).join(' ') : '';

    return (
      <div className={styles.backdrop} onClick={onDismiss} data-testid="rpe-backdrop">
        <div
          role="dialog"
          aria-label="Rate your effort"
          className={styles.v2Card}
          data-variant="v2"
          onClick={(e) => e.stopPropagation()}
        >
          <span className={styles.v2Handle} aria-hidden="true" />
          <div className={styles.v2Head}>
            <p className={styles.v2Title}>How hard did it feel?</p>
            {sub ? <p className={styles.v2Sub}>{sub}</p> : null}
          </div>
          <div className={styles.v2AnchorsAbove}>
            {RPE_V2_ANCHORS_ABOVE.map((a) => (
              <span key={a.value} id={anchorId(a.value)} className={styles.v2Anchor} aria-hidden="true">
                {a.text}
              </span>
            ))}
          </div>
          <div className={styles.v2Pills}>
            {RPE_VALUES.map((value) => (
              <button
                key={value}
                type="button"
                className={[styles.v2Pill, picked === value ? styles.v2Picked : ''].filter(Boolean).join(' ')}
                onClick={() => handlePick(value)}
                aria-label={`Effort ${value} of 10`}
                aria-pressed={picked === value}
                aria-describedby={RPE_V2_ANCHOR_VALUES.has(value) ? anchorId(value) : undefined}
              >
                {value}
                <span
                  className={[styles.v2Bar, styles[`v2Bar-${rpeBand(value)}`]].join(' ')}
                  aria-hidden="true"
                />
              </button>
            ))}
          </div>
          <div className={styles.v2AnchorsBelow}>
            {RPE_V2_ANCHORS_BELOW.map((a) => (
              <span
                key={a.value}
                id={anchorId(a.value)}
                className={[styles.v2Anchor, a.value === 7 ? styles.v2AnchorHard : styles.v2AnchorMax].join(' ')}
                aria-hidden="true"
              >
                {a.text}
              </span>
            ))}
          </div>
          {distanceCapture && (
            <div className={styles.v2Distance}>
              <p className={styles.v2DistanceLabel}>What did you actually run?</p>
              <div className={styles.v2DistanceRow}>
                <input
                  className={styles.v2Input}
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.1"
                  value={distText}
                  onChange={(e) => setDistText(e.target.value)}
                  aria-label="Actual distance"
                  aria-describedby={describedBy || undefined}
                />
                <span id={unitId} className={styles.v2Unit}>
                  {distUnit}
                </span>
              </div>
              {planned ? (
                <p id={plannedId} className={styles.v2Planned}>
                  {planned}
                </p>
              ) : null}
            </div>
          )}
          {captureMode && (
            <button type="button" className={styles.v2Save} onClick={handleSave} disabled={!canSave}>
              Save
            </button>
          )}
          <button type="button" className={styles.v2Skip} onClick={onDismiss}>
            Skip
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.backdrop} onClick={onDismiss} data-testid="rpe-backdrop">
      <div
        role="dialog"
        aria-label="Rate your effort"
        className={styles.card}
        onClick={(e) => e.stopPropagation()}
      >
        <p className={styles.question}>How hard did it feel?</p>
        <p className={styles.sub}>
          {workoutLabel ? `${workoutLabel} · ` : ''}Rate your perceived exertion
        </p>
        <div className={styles.chips}>
          {RPE_VALUES.map((value) => {
            const anchor = RPE_ANCHORS[value];
            const anchorId = `${anchorIdBase}-anchor-${value}`;
            const anchorEl = anchor ? (
              <span
                id={anchorId}
                className={[
                  styles.anchor,
                  anchor.place === 'above' ? styles.anchorAbove : styles.anchorBelow,
                  anchor.align === 'start' ? styles.anchorStart : styles.anchorEnd,
                ].join(' ')}
                aria-hidden="true"
              >
                {anchor.text}
              </span>
            ) : null;
            return (
              <div key={value} className={styles.cell}>
                {anchor?.place === 'above' && anchorEl}
                <button
                  type="button"
                  className={[
                    styles.chip,
                    styles[`band-${rpeBand(value)}`],
                    picked === value ? styles.picked : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => handlePick(value)}
                  aria-label={`Effort ${value} of 10`}
                  aria-describedby={anchor ? anchorId : undefined}
                >
                  {value}
                </button>
                {anchor?.place === 'below' && anchorEl}
              </div>
            );
          })}
        </div>
        {distanceCapture && (
          <div className={styles.distance}>
            <p className={styles.distanceLabel}>What did you actually run?</p>
            <div className={styles.distanceRow}>
              <input
                className={styles.distanceInput}
                type="number"
                inputMode="decimal"
                min="0"
                step="0.1"
                value={distText}
                onChange={(e) => setDistText(e.target.value)}
                aria-label="Actual distance"
              />
              <select
                className={styles.unitSelect}
                value={distUnit}
                onChange={(e) => setDistUnit(e.target.value === 'km' ? 'km' : 'mi')}
                aria-label="Distance unit"
              >
                <option value="mi">mi</option>
                <option value="km">km</option>
              </select>
            </div>
          </div>
        )}
        {/* F-110(a) — CAPTURE mode: one explicit Save commits the selected RPE
            AND the typed distance atomically, then the parent closes. F-110(b):
            a full-width primary action sits clear of the "Ask a coach" FAB (the
            sheet also z-orders above it — see the module CSS). */}
        {captureMode && (
          <button
            type="button"
            className={styles.commitSave}
            onClick={handleSave}
            disabled={!canSave}
          >
            Save
          </button>
        )}
        <button type="button" className={styles.skip} onClick={onDismiss}>
          Skip
        </button>
      </div>
    </div>
  );
}
